import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, GraduationCap, BookOpen, Wrench } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const Education = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["industries/education"]?.title} description={seoData["industries/education"]?.description} path="/industries/education" keywords={seoData["industries/education"]?.keywords || []} />

      <PageHero
        badge="Industries We Serve"
        title="Education"
        description="Coating and repair services for schools, colleges, and universities. Floor coatings, roof repair, tank coating, and facility maintenance for educational institutions across California."
        backgroundKey="education"
        buttonText="Get Industry Consultation"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Industry Challenges We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Gymnasium and cafeteria floor wear and damage',
              'Roof leaks and membrane failure on campus buildings',
              'Water tank and boiler system corrosion',
              'Parking structure concrete deterioration',
              'Restroom and locker room floor degradation',
              'Deferred maintenance creating safety hazards'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-blue-50 rounded-lg">
                <GraduationCap className="text-blue-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-blue-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <BookOpen className="text-blue-600 mr-3 flex-shrink-0" size={22} />
                  Campus Floor Systems
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Durable floor coating systems for gymnasiums, cafeterias, hallways, locker rooms, and maintenance areas. Slip-resistant, easy-to-clean surfaces that withstand heavy foot traffic and institutional cleaning chemicals.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <BookOpen className="text-blue-600 mr-3 flex-shrink-0" size={22} />
                  Roof Coating & Waterproofing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Roof leak repair and reflective roof coating systems for campus buildings. Membrane encapsulation and waterproofing to extend roof life and reduce energy costs.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <BookOpen className="text-blue-600 mr-3 flex-shrink-0" size={22} />
                  Water System Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Water tank lining, boiler repair, and pipe coating for campus water and heating systems. NSF 61 certified coatings for potable water storage.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <BookOpen className="text-blue-600 mr-3 flex-shrink-0" size={22} />
                  Structural & Concrete Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Parking structure repair, exterior concrete restoration, and walkway coating for campus infrastructure. Crack injection, spall repair, and protective coatings.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS for Education?</h2>
              <div className="space-y-3">
                {['Summer and break scheduling to avoid disruption to students and staff',
                  'Low-odor and low-VOC coating systems for occupied buildings',
                  'DSA and public works compliance experience',
                  'Slip-resistant surfaces for safety in wet areas',
                  'Cost-effective solutions for limited budgets',
                  'Extend facility life and defer capital replacement'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-blue-700 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Campus Maintenance Partner</h3>
              <p className="text-blue-100 mb-6">BCS works with school districts and universities across California. We schedule work during breaks and use low-odor systems to minimize disruption to the educational environment.</p>
              <Button className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/services/floor-repair')}>
                View Floor Coatings <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Protect Your Campus Infrastructure</h2>
          <p className="text-blue-100 mb-8">Contact us for a campus facility assessment and summer maintenance planning.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Education;
